import Tone from 'tone';
import {chainFactory} from './EnvelopeChainFactory.js';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import {mate} from 'genetix/Flip.js';
import {AbstractParameter, IntegerParameter} from 'parameters/AbstractParameter.js';
import {loadParameterFromJson, saveParameterToJson} from 'parameters/parameterJsonFunctions.js';
import Modulator from './Modulator.js';
import {ENVELOPE} from 'constants/mod-types.js';

class Envelope extends Modulator {
    constructor(sequencerEngine, mixSession, context, number) {
        super(sequencerEngine, mixSession, context, number);

        this.lastValue = undefined;
        this.lastStep = undefined;

        this.envelope = new Tone.ScaledEnvelope();

        this.meter = new Tone.Analyser('waveform', 32);
        this.envelope.connect(this.meter);

        this.envelope.min = 0;
        this.envelope.max = 1;


        this.parameters = [];
        this.setupParameters();

        this.savedFrequency = undefined;

        this.parameterIdToVoiceToNode = {};
    }

    get name() {
        return 'Env ' + this.number;
    }

    getType() {
        return ENVELOPE;
    }

    dispose() {
        this.envelope.dispose();
        this.meter.dispose();

        for (let i in this.parameterIdToVoiceToNode) {
            let voiceToChain = this.parameterIdToVoiceToNode[i];
            for (let voice in voiceToChain) {
                let chain = voiceToChain[voice];
                chain
                    .filter(s => s.dispose && s.input)
                    .forEach(s => s.dispose());
            }
        }
        this.parameterIdToVoiceToNode = undefined;
  
        this.envelope = undefined;
        this.meter = undefined;
        this.parameters = undefined;
    }

    getValue(step) {
        let {lastTriggerAttackRelease} = this;
        if (lastTriggerAttackRelease === undefined) {
            return 0;
        }

        let hasDuration = lastTriggerAttackRelease.duration !== undefined;
        let currentTime = Tone.Master.context.isOffline ?
            Tone.Master.context._currentTime : Tone.Master.context.currentTime;
        let timeOfTrigger= lastTriggerAttackRelease.time;
        let timeSinceTrigger = currentTime - timeOfTrigger;
        let timeOfRelease = lastTriggerAttackRelease.time + lastTriggerAttackRelease.duration;
        let timeSinceRelease = currentTime - timeSinceRelease;
        if (currentTime >= timeOfTrigger && (!hasDuration || currentTime <= timeOfRelease)) {
            // then we are current inside a trigger
            if (timeSinceTrigger <= this.attack / 1000.0) {
                // ATTACK STAGE
                let attackRatio = timeSinceTrigger / (this.attack / 1000.0);
                return attackRatio;
            }  else if (timeSinceTrigger <= (this.attack + this.decay) / 1000.0) {
                let decayTime = timeSinceTrigger - (this.attack / 1000.0); 
                let decayRatio = decayTime / (this.decay / 1000.0);
                return  1 + decayRatio * (this.sustain - 1);
            } else {
                return this.sustain;
            }
        }
        if (currentTime >= timeOfRelease && currentTime <= timeOfRelease + (this.release / 1000.0)) {
            let timeSinceRelease = currentTime - timeOfRelease;
            let releaseRatio = timeSinceRelease / (this.release / 1000.0);
            return this.sustain - this.sustain * releaseRatio;
        }

        return 0;

        /*
        if (step === this.lastStep) {
            return this.lastValue;
        }
        let value = this.meter.getValue()[0];
        this.lastStep = step;
        this.lastValue = value;
        return value;
        */
    }

    getChain(parameter, audioNode) {
        return [
            ... chainFactory.useChain(parameter, this.sequencerEngine),
            audioNode];
    }
    
    releaseChain(chain) {
        chainFactory.releaseChain(chain.slice(0, chain.length - 1), this.sequencerEngine);
    }

    getModulator() {
        return this.envelope;
    }

    triggerAttack(time, velocity) {
        time = time ||
            (Tone.Master.context.isOffline ?
             Tone.Master.context._currentTime :
             Tone.Master.context.currentTime);
        //this.envelope.cancel(time);
        this.envelope.triggerAttack(time, velocity);
            
        this.lastTriggerAttackRelease = {
            time,
            velocity
        };
    }

    triggerAttackRelease(duration, time=0, velocity) {
        time = Math.max(time, 0);
        //this.envelope.cancel(time);
        this.envelope.triggerAttackRelease(duration, time, velocity);
        this.lastTriggerAttackRelease = {duration, time, velocity};
    }

    triggerRelease(time) {
        this.envelope.triggerRelease(time);
        time = time ||
            (Tone.Master.context.isOffline ?
             Tone.Master.context._currentTime :
             Tone.Master.context.currentTime);
 
        if (this.lastTriggerAttackRelease) {
            this.lastTriggerAttackRelease =
                {
                    ... this.lastTriggerAttackRelease,
                    duration: time - this.lastTriggerAttackRelease.time
                };
        }
    }

    setupParameters() {
        this.attackParameter = new AbstractParameter(
            'Attack',
            this.envelope, (a, b) => a.attack = (b + 0.000001) / 1000.0,
            {
                min: 0,
                max: 10000,
                unit: 'ms',
                defaultValue: 15
            });

        this.registerParameter(this.attackParameter);

        this.decayParameter = new AbstractParameter(
            'Decay',
            this.envelope, (a, b) => a.decay = (b + 0.00001) / 1000.0,
            {
                min: 0,
                max: 10000,
                unit: 'ms',
                defaultValue: 1000
            });

        this.registerParameter(this.decayParameter);

        this.sustainParameter = new AbstractParameter(
            'Sustain',
            this.envelope, (a, b) => a.sustain = b, {
                min: 0,
                max: 1,
                unit: '',
                defaultValue: 0.9
            });
            
        this.registerParameter(this.sustainParameter);

        this.releaseParameter = new AbstractParameter(
            'Release',
            this.envelope,
            (a, b) => a.release = (b + 0.000001) / 1000.0,
            {
                min: 0,
                max: 10000,
                unit: 'ms',
                defaultValue: 150
            });

        this.registerParameter(this.releaseParameter);

        this.attackCurveParameter = new AbstractParameter(
            'Attack Curve',
            this.envelope, (a, b) => {
                a.attackCurve = b;
            },
            {
                min: 0,
                max: 10000,
                unit: '',
                defaultValue: 'linear'
            });

        this.registerParameter(this.attackCurveParameter);

        this.decayCurveParameter = new AbstractParameter(
            'Decay Curve',
            this.envelope, (a, b) => a.decayCurve = b,
            {
                min: 0,
                max: 10000,
                unit: '',
                defaultValue: 'linear'
            });

        this.registerParameter(this.decayCurveParameter);

        this.releaseCurveParameter = new AbstractParameter(
            'Release Curve',
            this.envelope, (a, b) => a.releaseCurve = b,
            {
                min: 0,
                max: 10000,
                unit: '',
                defaultValue: 'linear'
            });

        this.registerParameter(this.releaseCurveParameter);
   }

    refresh() {
        this.attack = this.attack;
        this.decay = this.decay;
        this.release = this.release;
        this.sustain = this.sustain;
    }

    get attack() {
        return this.attackParameter.get();
    }

    set attack(x) {
        this.attackParameter.set(x);
    }

    get decay() {
        return this.decayParameter.get();
    }

    set decay(x) {
        this.decayParameter.set(x);
    }

    get sustain() {
        return this.sustainParameter.get();
    }

    set sustain(x) {
        this.sustainParameter.set(x);
    }

    get release() {
        return this.releaseParameter.get();
    }

    set release(x) {
        this.releaseParameter.set(x);
    }

    get attackCurve() {
        return this.attackCurveParameter.get();
    }

    set attackCurve(x) {
        this.attackCurveParameter.set(x);
    }

    get decayCurve() {
        return this.decayCurveParameter.get();
    }

    set decayCurve(x) {
        this.decayCurveParameter.set(x);
    }

    get releaseCurve() {
        return this.releaseCurveParameter.get();
    }

    set releaseCurve(x) {
        this.releaseCurveParameter.set(x);
    }

    registerParameter(parameter) {
        parameter.setStore(this.store);
        parameter.setMixSession(this.mixSession);
        parameter.setContext(this.context);
        parameter.setTrack(this.context.track);
        this.parameters.push(parameter);
    }

    getJson() {
        let json = {
            attack: this.attack,
            decay: this.decay,
            sustain: this.sustain,
            release: this.release,
            attackCurve: this.attackCurve,
            decayCurve: this.decayCurve,
            releaseCurve: this.releaseCurve,
        };

        json = saveParameterToJson(json, 'attackParameter', this.attackParameter);
        json = saveParameterToJson(json, 'decayParameter', this.decayParameter);
        json = saveParameterToJson(json, 'sustainParameter', this.sustainParameter);
        json = saveParameterToJson(json, 'releaseParameter', this.releaseParameter);
        json = saveParameterToJson(json, 'attackCurveParameter', this.attackCurveParameter);
        json = saveParameterToJson(json, 'decayCurveParameter', this.decayCurveParameter);
        json = saveParameterToJson(json, 'releaseCurveParameter', this.releaseCurveParameter);
        return json;
    }

    fromJson(json) {
        this.disconnectAllModulators(); 
        let bulk = {};
        bulkUpdate(this.attackParameter , json.attack, bulk);
        bulkUpdate(this.decayParameter , json.decay, bulk);
        bulkUpdate(this.sustainParameter , json.sustain, bulk);
        bulkUpdate(this.releaseParameter , json.release, bulk);
        if (json.attackCurve) {
            bulkUpdate(this.attackCurveParameter , json.attackCurve, bulk);
        }
        if (json.decayCurve) {
            bulkUpdate(this.decayCurveParameter , json.decayCurve, bulk);
        }
        if (json.releaseCurve) {
            bulkUpdate(this.releaseCurveParameter , json.releaseCurve, bulk);
        }

        bulk = {
            ... bulk,
            ... loadParameterFromJson(json, 'attackParameter', this.attackParameter, this.sequencerEngine, false, true),
            ... loadParameterFromJson(json, 'decayParameter', this.decayParameter, this.sequencerEngine, false, true),
            ... loadParameterFromJson(json, 'sustainParameter', this.sustainParameter, this.sequencerEngine, false, true),
            ... loadParameterFromJson(json, 'releaseParameter', this.releaseParameter, this.sequencerEngine, false, true),
            ... loadParameterFromJson(json, 'attackCurveParameter', this.attackCurveParameter, this.sequencerEngine, false, true),
            ... loadParameterFromJson(json, 'decayCurveParameter', this.decayCurveParameter, this.sequencerEngine, false, true),
            ... loadParameterFromJson(json, 'releaseCurveParameter', this.releaseCurveParameter, this.sequencerEngine, false, true),
        };
    }

    refresh() {
        /*
        this.parameters.filter(p => p.isConnectedToAnyLFO())
            .forEach(p => p.callSetter());
            */
    }

    getScaleFromChain(chain) {
        return chain[0];
    }

    getDepthFromChain(chain) {
        return chain[1];
    }

    mate(partner, probability) {
        this.mateParameter(this.attackParameter, partner.attackParameter, probability);
        this.mateParameter(this.decayParameter, partner.decayParameter, probability);
        this.mateParameter(this.sustainParameter, partner.sustainParameter, probability);
        this.mateParameter(this.releaseParameter, partner.releaseParameter, probability);
    }

    mateParameter(param1, param2, probability) {
        mate(this.sequencerEngine, param1, param2, probability);
    }
}

export default Envelope;
